import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { BASE_URL, breadcrumbLD } from '../seo/routeMetadata';
import { LEARN_ARTICLES } from '../seo/seoContent';

const readingMinutes = (art) => {
  const words = (art.body || []).reduce((n, s) => n + (s.p || '').split(/\s+/).length, 0)
    + (art.aeoAnswer || '').split(/\s+/).length;
  return Math.max(1, Math.round(words / 200));
};

const fmtDateLong = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return iso; }
};

const fmtDateShort = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
  } catch { return iso; }
};

const LearnArticlePage = () => {
  const { slug } = useParams();
  const art = LEARN_ARTICLES[slug];

  if (!art) {
    return <Navigate to="/learn" replace />;
  }

  const url = `${BASE_URL}/learn/${slug}`;
  const jsonLd = [
    breadcrumbLD([
      { name: 'Home', path: '/' },
      { name: 'Learn', path: '/learn' },
      { name: art.title, path: `/learn/${slug}` },
    ]),
    {
      '@type': 'Article',
      headline: art.title,
      description: art.description,
      author: { '@id': `${BASE_URL}/#organization` },
      publisher: { '@id': `${BASE_URL}/#organization` },
      mainEntityOfPage: url,
      dateModified: art.lastUpdated,
      datePublished: art.lastUpdated,
    },
  ];

  return (
    <div className="min-h-screen bg-[#050810]">
      <SEO
        title={`${art.title} | AMP Predict Learn`}
        description={art.description}
        url={url}
        type="article"
        jsonLd={jsonLd}
      />
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        {/* Unified article header — breadcrumb, title, meta strip, and
            optional "What's new" pill all live in one visual block so the
            page opens cleanly instead of stacking 5 separate slabs. */}
        <header className="mb-10 pb-8 border-b border-[#1e2a45]">
          <nav aria-label="Breadcrumb" className="mb-6 text-xs text-gray-500">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2 text-gray-600">›</span>
            <Link to="/learn" className="hover:text-white">Learn</Link>
            <span className="mx-2 text-gray-600">›</span>
            <span className="text-gray-400 truncate">{art.title}</span>
          </nav>

          <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight mb-3" data-testid="page-h1">
            {art.h1}
          </h1>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mb-6">
            {art.lastUpdated && (
              <>
                <span>Updated <time dateTime={art.lastUpdated}>{fmtDateLong(art.lastUpdated)}</time></span>
                <span aria-hidden="true">·</span>
              </>
            )}
            <span>{readingMinutes(art)} min read</span>
            <span aria-hidden="true">·</span>
            <Link to="/learn" className="hover:text-white">Learn hub</Link>
            {Array.isArray(art.whatsNew) && art.whatsNew.length > 0 && (
              <>
                <span aria-hidden="true">·</span>
                <a
                  href="#whats-new"
                  data-testid="whats-new-anchor"
                  className="inline-flex items-center gap-1 text-[#3B82F6] hover:underline"
                >
                  <Sparkles className="h-3 w-3" />
                  Recently updated
                </a>
              </>
            )}
          </div>

          <p className="text-lg text-gray-200 leading-relaxed">{art.aeoAnswer}</p>
        </header>

        {Array.isArray(art.whatsNew) && art.whatsNew.length > 0 && (
          <aside
            id="whats-new"
            data-testid="whats-new"
            className="mb-10 rounded-xl border border-[#3B82F6]/25 bg-[#3B82F6]/5 px-5 py-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-[#3B82F6]" />
              <h2 className="text-xs font-semibold text-[#3B82F6] uppercase tracking-wider">What&rsquo;s new</h2>
            </div>
            <ul className="space-y-1.5">
              {art.whatsNew.slice(0, 3).map((entry, idx) => (
                <li key={idx} className="text-sm text-gray-300 leading-relaxed">
                  <time dateTime={entry.date} className="text-gray-500 mr-2">
                    {fmtDateShort(entry.date)}
                  </time>
                  {entry.note}
                </li>
              ))}
            </ul>
          </aside>
        )}

        <article className="prose prose-invert max-w-none space-y-8">
          {art.body.map((b) => (
            <section key={b.h2}>
              <h2 className="text-xl font-semibold text-white mb-3">{b.h2}</h2>
              <p className="text-gray-300 leading-relaxed">{b.p}</p>
            </section>
          ))}
        </article>

        {art.related && art.related.length > 0 && (
          <div className="mt-16 pt-8 border-t border-[#1e2a45]">
            <h3 className="text-lg font-semibold text-white mb-4">Related articles</h3>
            <div className="space-y-2">
              {art.related.map((rel) => {
                const relArt = LEARN_ARTICLES[rel];
                if (!relArt) return null;
                return (
                  <Link
                    key={rel}
                    to={`/learn/${rel}`}
                    className="flex items-center justify-between rounded-lg bg-[#131b2e] hover:bg-[#1a2440] p-3 text-sm"
                  >
                    <span className="text-white">{relArt.title}</span>
                    <ArrowRight className="h-4 w-4 text-[#3B82F6]" />
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-12 rounded-2xl border border-[#F97316]/30 bg-gradient-to-r from-[#F97316]/10 to-[#3B82F6]/10 p-6 text-center">
          <p className="text-gray-300 mb-4">Ready to practice with virtual TK¢ tokens?</p>
          <Link to="/" className="inline-block bg-[#F97316] hover:bg-[#EA580C] text-white px-6 py-3 rounded-full font-semibold" data-testid="learn-cta-explore">
            Explore live markets
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default LearnArticlePage;
